/* eslint-disable no-bitwise */

/**
/* Copyright 2023-2024 nmdp.  All rights reserved.
 * See LICENSE in the project root for license information.
 *
 * This module provides utility functions for working with GUIDs (Globally Unique Identifiers).
 * It includes functions to normalize, compare, or generate new GUIDs
 */

/**
 * Normalizes a GUID by converting it to lowercase and removing curly braces and hyphens.
 *
 * @param guid - The GUID to normalize.
 * @returns The normalized GUID, or `undefined` if the input is `undefined`.
 */
export const normalizeGuid = (guid?: string): string | undefined =>
  guid?.toLowerCase().replace(/[{\-}]/g, '');

/**
 * Compares two GUIDs for equality after normalizing them.
 *
 * @param guid1 - The first GUID to compare.
 * @param guid2 - The second GUID to compare.
 * @returns `true` if the normalized GUIDs are equal, `false` otherwise.
 */
/**
 * Compares two GUID strings for equality after normalizing them.
 *
 * @param guid1 - The first GUID string to compare. Optional.
 * @param guid2 - The second GUID string to compare. Optional.
 * @returns `true` if the normalized GUIDs are equal, `false` otherwise.
 */
export const guidEquals = (guid1?: string, guid2?: string): boolean =>
  normalizeGuid(guid1) === normalizeGuid(guid2);

/**
 * Generates a new GUID.
 *
 * @returns A newly generated GUID.
 */
export const newGuid = () => {
  try {
    const ret =
      crypto.randomUUID() ??
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    return ret;
  } catch (e) {
    return undefined;
  }
};
